<template>
  <div class="extras-mg-tl">
    <div v-if="cargando" class="extras-mg-tl__estado">Cargando…</div>

    <div v-else-if="!view.start_date_field" class="extras-mg-tl__estado">
      Elige arriba el campo de fecha donde empieza cada barra.
    </div>

    <div v-else-if="!barras.length" class="extras-mg-tl__estado">
      Ninguna fila tiene fecha de inicio, asi que no hay nada que dibujar.
    </div>

    <template v-else>
      <div class="extras-mg-tl__barra-superior">
        <span>{{ rotulo(minimo) }} — {{ rotulo(maximo) }}</span>
        <span class="extras-mg-tl__cuenta">{{ barras.length }} filas</span>
        <span v-if="sinFecha" class="extras-mg-tl__cuenta"
          >{{ sinFecha }} sin fecha de inicio</span
        >
        <span v-if="hayMasFilas" class="extras-mg-tl__tope"
          >Se muestran las primeras {{ TOPE_TOTAL }} filas de la vista</span
        >
      </div>

      <div class="extras-mg-tl__cuerpo">
        <div class="extras-mg-tl__eje">
          <div
            v-for="m in marcas"
            :key="m.ms"
            class="extras-mg-tl__marca"
            :class="{ 'extras-mg-tl__marca--fuerte': m.fuerte }"
            :style="{ left: m.porcentaje + '%' }"
          >
            <span class="extras-mg-tl__marca-texto">{{ m.etiqueta }}</span>
          </div>
        </div>

        <div class="extras-mg-tl__filas">
          <!-- Las lineas verticales viven detras de las barras para que se pueda
               leer donde cae cada una sin tener que pasar el raton. -->
          <div class="extras-mg-tl__rejilla">
            <div
              v-for="m in marcas"
              :key="'g' + m.ms"
              class="extras-mg-tl__linea-guia"
              :class="{ 'extras-mg-tl__linea-guia--fuerte': m.fuerte }"
              :style="{ left: m.porcentaje + '%' }"
            ></div>
            <div
              v-if="hoy !== null"
              class="extras-mg-tl__hoy"
              :style="{ left: hoy + '%' }"
              title="Hoy"
            ></div>
          </div>

          <div
            v-for="b in barras"
            :key="b.fila.id"
            class="extras-mg-tl__linea"
            @click="abrirFila(b.fila)"
          >
            <div class="extras-mg-tl__etiqueta" :title="b.titulo">
              {{ b.titulo }}
            </div>
            <div class="extras-mg-tl__pista">
              <div
                class="extras-mg-tl__barra"
                :class="b.color ? 'background-color--' + b.color : ''"
                :style="{ left: b.izquierda + '%', width: b.ancho + '%' }"
                :title="`${b.inicio} → ${b.fin} (${b.dias} d)`"
              >
                <span v-if="b.ancho > 14" class="extras-mg-tl__barra-texto">
                  {{ b.inicio }} → {{ b.fin }}
                </span>
              </div>
              <!-- Si la barra es muy corta el texto no cabe dentro, asi que se
                   pone al lado. Antes solo estaba en el title y habia que
                   adivinar pasando el raton. -->
              <span
                v-if="b.ancho <= 14"
                class="extras-mg-tl__barra-fuera"
                :style="barraFueraEstilo(b)"
              >
                {{ b.inicio }}<template v-if="b.dias > 1"> → {{ b.fin }}</template>
              </span>
            </div>
          </div>
        </div>
      </div>
    </template>

    <RowEditModal
      ref="rowEditModal"
      enable-navigation
      :database="database"
      :table="table"
      :view="view"
      :all-fields-in-table="fields"
      :primary-is-sortable="true"
      :visible-fields="camposDeTarjeta"
      :hidden-fields="camposOcultos"
      :rows="filas"
      :read-only="noSePuedeEditar"
      :show-hidden-fields="showHiddenFieldsInRowModal"
      @hidden="$emit('selected-row', undefined)"
      @toggle-hidden-fields-visibility="
        showHiddenFieldsInRowModal = !showHiddenFieldsInRowModal
      "
      @update="actualizarValor"
      @field-updated="$emit('refresh', $event)"
      @field-deleted="$emit('refresh')"
      @field-created="$emit('refresh')"
      @refresh-row="refrescarFila"
    ></RowEditModal>
  </div>
</template>

<script>
import moment from '@baserow/modules/core/moment'
import { getFieldTimezone } from '@baserow/modules/database/utils/date'

import vistaMG from '@extras_mg/mixins/vistaMG'

const DIA = 24 * 60 * 60 * 1000
const MESES = [
  'ene', 'feb', 'mar', 'abr', 'may', 'jun',
  'jul', 'ago', 'sep', 'oct', 'nov', 'dic',
]

export default {
  name: 'TimelineView',
  mixins: [vistaMG],
  props: {
    database: { type: Object, required: true },
    table: { type: Object, required: true },
    view: { type: Object, required: true },
    fields: { type: Array, required: true },
    readOnly: { type: Boolean, required: true },
    storePrefix: { type: String, required: false, default: '' },
  },
  emits: ['refresh', 'selected-row'],
  data() {
    return { rutaDeFilas: 'timeline-mg' }
  },
  computed: {
    campoDeInicio() {
      return this.fields.find((f) => f.id === this.view.start_date_field)
    },
    campoDeFin() {
      return this.fields.find((f) => f.id === this.view.end_date_field)
    },
    /**
     * Cada fila con fecha de inicio se vuelve una barra. Sin fecha de fin la
     * barra dura un dia, que es mas honesto que estirarla hasta hoy.
     */
    tramos() {
      const inicio = `field_${this.view.start_date_field}`
      const fin = this.view.end_date_field
        ? `field_${this.view.end_date_field}`
        : null
      const salida = []
      for (const fila of this.filas) {
        const d1 = this.aFecha(fila[inicio], this.campoDeInicio)
        if (d1 === null) {
          continue
        }
        let d2 = fin ? this.aFecha(fila[fin], this.campoDeFin) : null
        if (d2 === null || d2 < d1) {
          d2 = d1
        }
        salida.push({ fila, d1, d2 })
      }
      return salida
    },
    sinFecha() {
      return this.filas.length - this.tramos.length
    },
    minimo() {
      return this.tramos.length
        ? Math.min(...this.tramos.map((t) => t.d1))
        : 0
    },
    maximo() {
      return this.tramos.length
        ? Math.max(...this.tramos.map((t) => t.d2))
        : 0
    },
    rango() {
      // Se suma un dia para que una barra de un solo dia tenga ancho visible.
      return Math.max(this.maximo - this.minimo + DIA, DIA)
    },
    barras() {
      return this.tramos.map((t) => {
        const izquierda = ((t.d1 - this.minimo) / this.rango) * 100
        const ancho = Math.max(((t.d2 - t.d1 + DIA) / this.rango) * 100, 1.2)
        return {
          fila: t.fila,
          titulo: this.titulo(t.fila),
          inicio: this.rotulo(t.d1),
          fin: this.rotulo(t.d2),
          dias: Math.round((t.d2 - t.d1) / DIA) + 1,
          color: this.colorDeFila(t.fila),
          izquierda,
          ancho,
        }
      })
    },
    /**
     * El eje se adapta al rango. Con un mes de datos, poner una sola marca
     * "oct 26" no dice nada: hacen falta dias. Con dos años, una marca por dia
     * es ilegible. Se elige el paso segun cuantos dias abarca la vista.
     */
    marcas() {
      if (!this.tramos.length) {
        return []
      }
      const dias = this.rango / DIA
      const salida = []
      const empujar = (ms, etiqueta, fuerte) => {
        const p = ((ms - this.minimo) / this.rango) * 100
        if (p >= -0.5 && p <= 100.5) {
          salida.push({ ms, etiqueta, porcentaje: p, fuerte })
        }
      }

      if (dias <= 45) {
        // Dia a dia, saltando para no encimar etiquetas.
        const salto = dias <= 16 ? 1 : dias <= 31 ? 2 : 3
        const d0 = new Date(this.minimo)
        for (let i = 0; i <= dias; i += salto) {
          const d = new Date(d0.getFullYear(), d0.getMonth(), d0.getDate() + i)
          empujar(d.getTime(), `${d.getDate()} ${MESES[d.getMonth()]}`, d.getDate() === 1)
        }
      } else if (dias <= 400) {
        // Semanal (lunes) hasta ~4 meses, luego mensual.
        if (dias <= 120) {
          const d0 = new Date(this.minimo)
          const desplazamiento = (d0.getDay() + 6) % 7
          let cur = new Date(
            d0.getFullYear(), d0.getMonth(), d0.getDate() - desplazamiento
          )
          while (cur.getTime() <= this.maximo) {
            empujar(
              cur.getTime(),
              `${cur.getDate()} ${MESES[cur.getMonth()]}`,
              cur.getDate() <= 7
            )
            cur = new Date(
              cur.getFullYear(), cur.getMonth(), cur.getDate() + 7
            )
          }
        } else {
          const d0 = new Date(this.minimo)
          let cur = new Date(d0.getFullYear(), d0.getMonth(), 1)
          while (cur.getTime() <= this.maximo) {
            empujar(
              cur.getTime(),
              `${MESES[cur.getMonth()]} ${String(cur.getFullYear()).slice(2)}`,
              cur.getMonth() === 0
            )
            cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1)
          }
        }
      } else {
        // Trimestral, para no pasar de ~30 marcas.
        const d0 = new Date(this.minimo)
        let cur = new Date(d0.getFullYear(), Math.floor(d0.getMonth() / 3) * 3, 1)
        while (cur.getTime() <= this.maximo) {
          empujar(
            cur.getTime(),
            `${MESES[cur.getMonth()]} ${String(cur.getFullYear()).slice(2)}`,
            cur.getMonth() === 0
          )
          cur = new Date(cur.getFullYear(), cur.getMonth() + 3, 1)
        }
      }
      return salida
    },
    /** Posicion de hoy en la cinta, o null si cae fuera del rango. */
    hoy() {
      if (!this.tramos.length) {
        return null
      }
      const h = new Date()
      const ms = new Date(h.getFullYear(), h.getMonth(), h.getDate()).getTime()
      if (ms < this.minimo || ms > this.maximo + DIA) {
        return null
      }
      return ((ms - this.minimo) / this.rango) * 100
    },
  },
  watch: {
    'view.start_date_field'() {
      this.traer()
    },
    'view.end_date_field'() {
      this.traer()
    },
  },
  methods: {
    /**
     * El dia en el que cae la fecha, como marca de tiempo local a medianoche.
     *
     * Un campo de solo fecha se parte en sus numeros y no se le pasa la cadena a
     * Date: new Date('2026-08-07') se interpreta en UTC y en Cancun (UTC-5)
     * caeria un dia antes. Un campo CON hora llega en UTC, asi que primero se
     * lleva a la zona del campo —la misma que usa Baserow para pintar la celda—
     * y de ahi se toma el dia.
     */
    aFecha(valor, campo) {
      if (!valor || typeof valor !== 'string') {
        return null
      }
      let iso = valor.split('T')[0]
      if (campo && campo.date_include_time) {
        const m = moment.utc(valor, moment.ISO_8601, true)
        if (!m.isValid()) {
          return null
        }
        const zona = getFieldTimezone(campo)
        iso = (zona ? m.tz(zona) : m).format('YYYY-MM-DD')
      }
      const partes = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/)
      if (!partes) {
        return null
      }
      return new Date(+partes[1], +partes[2] - 1, +partes[3]).getTime()
    },
    /**
     * Coloca el texto a la derecha de la barra, salvo que la barra este tan a
     * la derecha que el texto se saldria: entonces va a su izquierda.
     */
    barraFueraEstilo(b) {
      const finBarra = b.izquierda + b.ancho
      return finBarra > 78
        ? { right: 100 - b.izquierda + 1 + '%', textAlign: 'right' }
        : { left: finBarra + 1 + '%' }
    },
    rotulo(ms) {
      if (!ms) {
        return ''
      }
      const d = new Date(ms)
      return `${String(d.getDate()).padStart(2, '0')}/${MESES[d.getMonth()]}/${d.getFullYear()}`
    },
  },
}
</script>

<style lang="scss">
.extras-mg-tl {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.extras-mg-tl__estado {
  padding: 16px;
  font-size: 13px;
  opacity: 0.7;
}

.extras-mg-tl__barra-superior {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 8px 12px;
  font-size: 12px;
  opacity: 0.8;
}

.extras-mg-tl__cuenta {
  opacity: 0.65;
  font-size: 11px;
}

.extras-mg-tl__tope {
  color: #b45309;
  font-size: 11px;
  margin-left: auto;
}

.extras-mg-tl__cuerpo {
  flex: 1;
  overflow: auto;
  padding: 0 12px 12px;
}

/* El ancho de la columna de titulos se comparte entre el eje y las filas.
   Si se cambia aqui hay que cambiarlo en __etiqueta. */
.extras-mg-tl__eje {
  position: relative;
  height: 20px;
  margin-left: 220px;
  border-bottom: 1px solid;
  border-bottom-color: color-mix(in srgb, currentColor 20%, transparent);
}

.extras-mg-tl__marca {
  position: absolute;
  top: 2px;
  font-size: 10px;
  white-space: nowrap;
  opacity: 0.5;
}

.extras-mg-tl__marca--fuerte {
  opacity: 0.85;
  font-weight: 600;
}

.extras-mg-tl__marca-texto {
  transform: translateX(-50%);
  display: inline-block;
}

.extras-mg-tl__filas {
  position: relative;
  padding-top: 4px;
}

/* Las guias van detras de las barras, ancladas a la misma pista. */
.extras-mg-tl__rejilla {
  position: absolute;
  left: 220px;
  right: 0;
  top: 0;
  bottom: 0;
  pointer-events: none;
}

.extras-mg-tl__linea-guia {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  background: color-mix(in srgb, currentColor 8%, transparent);
}

.extras-mg-tl__linea-guia--fuerte {
  background: color-mix(in srgb, currentColor 18%, transparent);
}

.extras-mg-tl__hoy {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #e0724a;
  opacity: 0.75;
}

.extras-mg-tl__linea {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 26px;
  cursor: pointer;
  position: relative;
}

.extras-mg-tl__linea:hover {
  background: color-mix(in srgb, currentColor 6%, transparent);
}

.extras-mg-tl__etiqueta {
  flex: 0 0 212px;
  font-size: 11px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.extras-mg-tl__pista {
  flex: 1;
  position: relative;
  height: 18px;
}

/* Color derivado del texto, no un azul fijo: el generador de tema de Mandala
   es monocromo y convierte cualquier color de marca, asi que un #5190ef
   terminaba en blanco plano. Con currentColor se ve bien en claro y en oscuro
   sin depender de esa conversion. */
.extras-mg-tl__barra {
  position: absolute;
  top: 2px;
  height: 14px;
  border-radius: 7px;
  /* Cuando la vista lleva decoracion de color, la barra usa el color de la fila
     (clase background-color--X del core). Sin decoracion, hereda como antes. */
  &:not([class*='background-color--']) {
    background: color-mix(in srgb, currentColor 45%, transparent);
  }
  border: 1px solid;
  border-color: color-mix(in srgb, currentColor 60%, transparent);
  min-width: 4px;
  display: flex;
  align-items: center;
  overflow: hidden;
}

.extras-mg-tl__barra-texto {
  font-size: 10px;
  padding: 0 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  opacity: 0.95;
}

.extras-mg-tl__barra-fuera {
  position: absolute;
  top: 3px;
  font-size: 10px;
  white-space: nowrap;
  opacity: 0.6;
}
</style>
